<template>
  <div class="homepage-hero">
    <div class="homepage-hero__content">
      <h1
        class="homepage-hero__title"
        v-html="$t('homepage_title')"
      />
      <div class="homepage-hero__intro">
        <p
          class="homepage-hero__lead"
          v-html="$t('homepage_lead')"
        />
        <div class="homepage-hero__actions">
          <AppButton
            light
            bold
            class="homepage-hero__button homepage-hero__button--start"
            @click="$emit('showPricing')"
          >
            {{ $t('start_sharing') }}
          </AppButton>
          <AppButton
            class="homepage-hero__button homepage-hero__button--discover desktop"
            secondary
            @click="$emit('showMore')"
          >
            {{ $t('discover_more') }}
          </AppButton>
          <AppButton
            class="homepage-hero__button homepage-hero__button--free-demo mobile"
            secondary
            @click="$emit('startDemo')"
          >
            {{ $t('try_our_free_demo') }}
          </AppButton>
        </div>

        <TrustpilotReview class="homepage-hero__reviews" />

        <AuthorityBadgeCustomers class="homepage-hero__authority-badge-customers" />

        <AppButton
          class="homepage-hero__button homepage-hero__button--discover mobile"
          tertiary
          @click="$emit('showMore')"
        >
          {{ $t('discover_more') }}
        </AppButton>
      </div>
    </div>
    <div class="homepage-hero__image-wrapper">
      <NuxtImg
        src="/images/homepage-hero-visual-2026-6.webp"
        format="webp"
        quality="55"
        alt="momentshare party"
        loading="eager"
        class="homepage-hero__image"
        width="696"
        height="498"
        sizes="372px lg:640px"
        densities="1x 1.5x"
        fetchpriority="high"
      />
      <KnownFrom class="homepage-hero__known-from desktop" />
    </div>
  </div>
</template>

<script setup>
  import TrustpilotReview from '~/components/trustpilot-review.vue'

  defineEmits(['showMore', 'showPricing', 'startDemo'])

  const { t: $t } = useI18n()
</script>

<style lang="scss">
  .homepage-hero {
    display: flex;
    flex-direction: column;
    justify-content: center;

    @include breakpoint(desktop) {
      flex-direction: row;
    }
  }

  .homepage-hero__image-wrapper {
    position: relative;
    max-width: 500px;
    margin: 0 auto;
    width: 100%;
    height: 100%;

    @include breakpoint(desktop) {
      max-width: 100%;
      margin: 0;
      width: 50%;
      height: auto; // stretch to the text column so the logos can sit at its bottom edge
      flex-shrink: 0;
    }
  }

  .homepage-hero__known-from.known-from {
    position: absolute;
    right: 0;
    bottom: 0;
    flex-direction: row;
    align-items: center;
    gap: 24px;
  }

  .homepage-hero__known-from .known-from__images {
    margin: 0;
  }

  .homepage-hero__image {
    max-width: 500px;
    margin: 0 auto;
    width: 100%;
    height: 100%;

    @include breakpoint(desktop) {
      max-width: none;
      margin: 0;
      height: auto;
      position: absolute;
      width: 100%;
      object-fit: contain;
      top: -40px;
      right: -40px;
    }
  }

  .homepage-hero__content {
    display: flex;
    flex-direction: column;
    justify-content: center;

    @include breakpoint(lg) {
      justify-content: flex-start;
      text-shadow: none;
    }

    @include breakpoint(desktop) {
      flex-shrink: 0;
      width: 50%;
    }
  }

  .homepage-hero__title {
    text-align: center;

    @include breakpoint(desktop) {
      text-align: left;
    }
  }

  .homepage-hero__title > br {
    display: none;

    @include breakpoint(lg) {
      display: initial;
    }
  }

  .homepage-hero__intro {
    flex-shrink: 0;
    margin: 12px 0 0;
    display: flex;
    flex-direction: column;

    @include breakpoint(desktop) {
      margin: 20px 0 0;
    }
  }

  .homepage-hero__lead {
    font-size: 16px;
    font-weight: 300;
    line-height: 1.25em;
    text-align: center;
    order: 1;

    @include breakpoint(desktop) {
      text-align: left;
      max-width: 700px;
      font-weight: 400;
      font-size: 20px;
      order: 0;
    }
  }

  .homepage-hero__lead > br {
    display: none;

    @include breakpoint(lg) {
      display: initial;
    }
  }

  .homepage-hero__actions {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    margin: 24px auto 0;
    gap: 12px;
    width: 90%;
    order: 2;
    max-width: 400px;

    @include breakpoint(desktop) {
      width: 100%;
      flex-direction: row;
      margin: 30px 0 0;
      gap: 24px;
      order: 0;
      max-width: 100%;
    }
  }

  .homepage-hero__button--start {
    @include breakpoint(desktop) {
      padding-left: 54px;
      padding-right: 54px;
    }
  }

  .homepage-hero__button--discover {
    order: 4;
  }

  .homepage-hero__reviews {
    margin: 12px auto 0;
    order: 1;

    @include breakpoint(desktop) {
      margin: 32px 0 0;
      order: 0;
    }
  }

  .homepage-hero__authority-badge-customers.authority-badge-customers {
    order: 3;
    margin: 12px 0 0;

    @include breakpoint(desktop) {
      justify-content: flex-start;
      order: 0;
      margin: 16px 0 0;
    }
  }
</style>
